import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"

import SelectDescription from "components/forms/select-description"

import { Pagination } from "components"
import { useAuth } from "contexts/auth-context"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import { getDefaultOutletFilterValue, getOutletFilterOptions, resolveOutletFilter } from "helpers/general-helper"
import useBranchRepositories from "repositories/branch"
import useStockBelowMinimumRepositories from "repositories/stock-below-minimum"

const formatNumber = (value) =>
  Number(value ?? 0).toLocaleString("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })

export default function List() {
  const { isSuper, currentBranch, user, canFilterAllOutlets } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const isBranchLocked = !canFilterAllOutlets() && !!currentBranch?.id // ATALLA-403: pemegang permission filter-entity bebas pilih outlet entitasnya
  // 290726 (permintaan user): filter outlet memakai komponen bersama supaya opsi
  // grup "Semua Outlet" & "HO" (outlet 1 + 15) ikut tersedia — seperti di laporan
  // Sales Qty Setahun. Nilai filter bisa berupa sentinel grup (GRP_ALL/GRP_HO),
  // jadi harus di-resolve ke CSV id sebelum dikirim ke API.
  // Backend laporan ini SUDAH menerima CSV (whereIn + explode), jadi aman.
  const rawOutletId = searchParams.get("outlet_id") || ""
  const outletFilterValue = isBranchLocked
    ? String(currentBranch.id)
    : rawOutletId || getDefaultOutletFilterValue()
  const { outlet_id_csv: outletId } = resolveOutletFilter(outletFilterValue)
  const productModelFrom = searchParams.get("product_model_from") || ""
  const productModelTo = searchParams.get("product_model_to") || ""
  const batasBulan = searchParams.get("batas_bulan") || "3"
  const search = searchParams.get("search") || ""
  // ATALLA-459: paginasi server-side di level GROUP (product_model).
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "50"

  const [searchInput, setSearchInput] = useState(search)
  const [fromInput, setFromInput] = useState(productModelFrom)
  const [toInput, setToInput] = useState(productModelTo)

  useEffect(() => setSearchInput(search), [search])
  useEffect(() => setFromInput(productModelFrom), [productModelFrom])
  useEffect(() => setToInput(productModelTo), [productModelTo])

  // Untuk branch-locked user: auto-sync outletId ke URL kalau belum ada.
  // Untuk super user: kosongkan default, biar pilih dari dropdown.
  useEffect(() => {
    if (isBranchLocked && !searchParams.get("outlet_id") && currentBranch?.id) {
      const next = new URLSearchParams(searchParams)
      next.set("outlet_id", String(currentBranch.id))
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranch?.id, isBranchLocked])

  const { data: branchSelection, isLoading: isLoadingBranches } = useBranchRepositories({ paginate: false, excludeYoiboi: true, stockScope: true })
  const branchOptions = useMemo(() => {
    return Array.isArray(branchSelection) ? branchSelection : []
  }, [branchSelection])

  // 290726: outletId kini bisa berupa CSV (grup "Semua Outlet"/"HO"), jadi label
  // tidak selalu bisa dicocokkan ke SATU outlet. Untuk gabungan, pakai label grup
  // dari komponen filter supaya judul/export tidak jadi kosong.
  const outletLabel = useMemo(() => {
    const ids = String(outletId || "").split(",").filter(Boolean)
    if (ids.length > 1) {
      const opt = getOutletFilterOptions().find((o) => o.value === outletFilterValue)
      return opt?.label || `${ids.length} outlet`
    }
    const one = ids[0] ?? ""
    if (currentBranch?.id && String(currentBranch.id) === one && currentBranch?.name) {
      return currentBranch.name
    }
    const match = branchOptions.find((b) => String(b?.id ?? "") === one)
    return match?.name || ""
  }, [outletId, outletFilterValue, currentBranch?.id, currentBranch?.name, branchOptions])

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)
    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })
    // ATALLA-459: ganti filter → reset ke halaman 1 (kecuali saat mengubah "page").
    if (!Object.prototype.hasOwnProperty.call(nextValues, "page")) {
      nextParams.set("page", "1")
    }
    setSearchParams(nextParams)
  }

  const { data: reportData, isLoading } = useStockBelowMinimumRepositories({
    outlet_id: outletId,
    product_model_from: productModelFrom,
    product_model_to: productModelTo,
    batas_bulan: batasBulan,
    search,
    user_label: user?.name || "",
    outlet_label: outletLabel,
    // ATALLA-459: paginasi server-side.
    page,
    per_page: perPage,
    enabled: Boolean(outletId),
  })

  const groups = useMemo(() => {
    // ATALLA-459: backend baru sekaligus kompat lama — groups = halaman aktif.
    if (Array.isArray(reportData?.items?.data)) return reportData.items.data
    return reportData?.groups ?? []
  }, [reportData])
  const grandTotal = useMemo(() => reportData?.grand_total ?? {}, [reportData])

  // ATALLA-459: metadata paginator (pola sama dgn report/intercompany-sales/list.jsx).
  const pagination = reportData?.items

  // Ubah link paginator (absolute API url) → query-string lokal, pertahankan filter.
  const paginationLinks = useMemo(() => {
    const links = pagination?.links ?? []
    return links.map((link) => {
      if (!link?.url) return link
      const nextParams = new URLSearchParams(searchParams)
      try {
        const url = new URL(link.url, window.location.origin)
        const pageNumber = url.searchParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      } catch (error) {
        const urlParams = new URLSearchParams(String(link.url).split("?")[1] ?? "")
        const pageNumber = urlParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      }
      return { ...link, url: `?${nextParams.toString()}` }
    })
  }, [pagination, searchParams])

  const paginationFrom = useMemo(() => {
    if (pagination?.from) return pagination.from
    if (!pagination || Number(pagination.total ?? 0) === 0) return 0
    return (Number(pagination.current_page ?? 1) - 1) * Number(pagination.per_page ?? perPage) + 1
  }, [pagination, perPage])

  const paginationTo = useMemo(() => {
    if (pagination?.to) return pagination.to
    if (!pagination) return 0
    return Math.min(
      Number(pagination.current_page ?? 1) * Number(pagination.per_page ?? perPage),
      Number(pagination.total ?? 0)
    )
  }, [pagination, perPage])

  const handleApplyFilters = () => {
    mergeSearchParams({
      product_model_from: fromInput,
      product_model_to: toInput,
      search: searchInput,
    })
  }

  const handleExport = () => {
    if (!outletId) {
      toast.error("Outlet wajib diisi")
      return
    }
    const query = new URLSearchParams({
      outlet_id: outletId,
      batas_bulan: batasBulan,
    })
    if (productModelFrom) query.set("product_model_from", productModelFrom)
    if (productModelTo) query.set("product_model_to", productModelTo)
    if (search) query.set("search", search)
    if (user?.name) query.set("user_label", user.name)
    if (outletLabel) query.set("outlet_label", outletLabel)
    window.open(
      `${process.env.REACT_APP_INVENTORY_API_URL}/reports/stock-below-minimum/export?${query.toString()}`,
      "_blank"
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Laporan Stock di Bawah Minimum</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Item dengan stock akhir {"<"} rata-rata penjualan {batasBulan} bulan penuh sebelumnya.
            Toko: <span className="font-medium">{outletLabel || currentBranch?.name || outletId}</span>.
          </p>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end xl:justify-end">
          <div className="min-w-[200px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Outlet</label>
            <OutletFilterSelect
              value={outletFilterValue}
              onChange={(value) => {
                if (isBranchLocked) return
                mergeSearchParams({ outlet_id: value || null, page: "1" })
              }}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              title="Outlet"
              disabled={isBranchLocked}
            />
          </div>

          <div className="min-w-[180px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">GROUP dari</label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={fromInput}
              onChange={(event) => setFromInput(event.target.value)}
              placeholder="mis. atm 1001"
            />
          </div>

          <div className="min-w-[180px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">GROUP sampai</label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={toInput}
              onChange={(event) => setToInput(event.target.value)}
              placeholder="mis. atm 1002"
            />
          </div>

          <div className="min-w-[140px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Batas Bulan</label>
            <select
              value={batasBulan}
              onChange={(event) => mergeSearchParams({ batas_bulan: event.target.value })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {[1, 2, 3, 6, 12].map((v) => (
                <option key={v} value={v}>{v} Bulan</option>
              ))}
            </select>
          </div>

          <div className="min-w-[180px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Cari Nama Barang</label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Nama / kode unit"
            />
          </div>

          {/* ATALLA-459: jumlah GROUP per halaman */}
          <div className="min-w-[120px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Group / Halaman</label>
            <select
              value={perPage}
              onChange={(event) => mergeSearchParams({ per_page: event.target.value })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {["20", "50", "100", "200"].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleApplyFilters}
            className="px-4 py-2 text-sm font-semibold text-neutral-900 border border-neutral-300 rounded-lg hover:bg-neutral-100"
          >
            Terapkan
          </button>

          <button
            type="button"
            onClick={handleExport}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
          >
            Ekspor Excel
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-4">
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Item Di Bawah Minimum</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(grandTotal.total_items)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Group</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(grandTotal.total_groups)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Stock</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(grandTotal.stock)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total PO (Stock − Rata)</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(grandTotal.po)}</div>
        </div>
      </div>

      <div className="overflow-hidden bg-white border border-neutral-200 rounded-2xl">
        <div className="px-4 py-4 border-b border-neutral-200 bg-neutral-50">
          <div className="text-xs font-medium text-neutral-600">{reportData?.outlet_label || outletLabel || currentBranch?.name || outletId}</div>
          <div className="text-sm font-semibold text-neutral-900">{reportData?.report_title || "LAPORAN STOCK DI BAWAH MINIMUM"}</div>
          <div className="mt-1 text-xs text-neutral-500">
            {reportData?.group_label || "GROUP : (semua)"} • BATAS BULAN: {reportData?.batas_bulan || batasBulan} Bulan
            {reportData?.date ? ` • TGL: ${reportData.date} ${reportData.time}` : ""}
            {reportData?.user_label ? ` • USER ID: ${reportData.user_label}` : ""}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-100">
              <tr>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">GROUP</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">NAMA BARANG</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">STOCK</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">RATA-RATA</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">PO</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-neutral-500">Memuat data laporan...</td>
                </tr>
              )}

              {!isLoading && groups.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-neutral-500">
                    Tidak ada item di bawah minimum untuk filter ini.
                  </td>
                </tr>
              )}

              {!isLoading && groups.map((group, groupIdx) => (
                <>
                  {(group.items || []).map((item, itemIdx) => (
                    <tr
                      key={`${groupIdx}-${itemIdx}`}
                      className="border-t border-neutral-200 hover:bg-brand-50 transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-neutral-900">
                        {itemIdx === 0 ? group.group_name : ""}
                      </td>
                      <td className="px-4 py-3 text-neutral-800">{item.product_unit_name}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap text-neutral-900">
                        {formatNumber(item.stock)}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap text-neutral-700">
                        {formatNumber(item.rata_rata)}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap text-neutral-900">
                        {formatNumber(item.po)}
                      </td>
                    </tr>
                  ))}
                  <tr key={`${groupIdx}-subtotal`} className="border-t border-neutral-200 bg-neutral-50">
                    <td className="px-4 py-2" />
                    <td className="px-4 py-2 font-semibold text-right text-neutral-700">SUB TOTAL =</td>
                    <td className="px-4 py-2 text-right font-semibold text-neutral-900">
                      {formatNumber(group.sub_total_stock)}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-neutral-700">
                      {formatNumber(group.sub_total_rata)}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-neutral-900">
                      {formatNumber(group.sub_total_po)}
                    </td>
                  </tr>
                </>
              ))}
            </tbody>
            {!isLoading && groups.length > 0 && (
              <tfoot className="bg-neutral-100">
                <tr className="border-t-2 border-neutral-300">
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3 font-bold text-right text-neutral-900">GRAND TOTAL =</td>
                  <td className="px-4 py-3 font-bold text-right text-neutral-900">{formatNumber(grandTotal.stock)}</td>
                  <td className="px-4 py-3 font-bold text-right text-neutral-700">{formatNumber(grandTotal.rata)}</td>
                  <td className="px-4 py-3 font-bold text-right text-neutral-900">{formatNumber(grandTotal.po)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* ATALLA-459: kontrol paginasi server-side (level GROUP) */}
      {!isLoading && pagination && Number(pagination.total ?? 0) > 0 && (
        <div className="pt-2">
          <Pagination
            links={paginationLinks}
            from={paginationFrom}
            to={paginationTo}
            total={pagination?.total}
          />
        </div>
      )}
    </div>
  )
}
